// The eighteen and over gate.
//
// Lost Lands is an eighteen and over event, outfits at one are often revealing,
// and no photo here is refused for that. So the door asks once, records the answer
// in this browser, and sends anybody who says no somewhere else.

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

const KEY = 'festival-scheduler-age-ack';

// The gate wraps what it guards rather than floating over it. As an overlay the
// routes underneath mounted, solved and started network work behind a card that
// looked like a door, and their controls stayed reachable by keyboard.
export function AgeGate({ children, publicInfo }: { children: ReactNode; publicInfo?: ReactNode }) {
  const [answered, setAnswered] = useState<boolean | null>(null);
  const [refused, setRefused] = useState(false);
  const card = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      setAnswered(localStorage.getItem(KEY) === 'yes');
    } catch {
      setAnswered(false);
    }
  }, []);

  useEffect(() => {
    if (answered === false) card.current?.querySelector('button')?.focus();
  }, [answered, refused]);

  // Nothing renders until the stored answer has been read, so a confirmed
  // browser never flashes the gate and an unconfirmed one never flashes the app.
  if (answered === null) return null;
  if (answered) return <>{children}</>;

  const confirm = () => {
    try {
      localStorage.setItem(KEY, 'yes');
    } catch {
      // A browser refusing storage asks again next load, which is the safe way to fail.
    }
    setAnswered(true);
  };

  return (<>
    <div className="agegate">
      <div
        className="agecard"
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agegate-title"
        onKeyDown={(e) => {
          // The card is the whole document while it is open, so a tab off the
          // last control returns to the first rather than reaching nothing.
          if (e.key !== 'Tab') return;
          const focusable = card.current?.querySelectorAll('button');
          if (!focusable || focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        {refused ? (
          <>
            <h2 id="agegate-title">This app is 18+</h2>
            <p>You need to be 18 or over to use it.</p>
            <button className="wide ghost" onClick={() => setRefused(false)}>
              Back
            </button>
          </>
        ) : (
          <>
            <h2 id="agegate-title">Are you 18 or over?</h2>
            <p>Lost Lands is an 18+ event, and this app is 18+.</p>
            <p className="muted">Your answer is saved in this browser only.</p>
            <button className="primary wide" onClick={confirm}>
              Yes, I am 18 or over
            </button>
            <button className="wide ghost" onClick={() => setRefused(true)}>
              No
            </button>
          </>
        )}
      </div>
    </div>
    {publicInfo}
  </>);
}
